import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Divider, Typography, styled } from '@mui/material';
import { formatDistanceToNowStrict } from 'date-fns';

import { IAddress, IUnitShiftResource } from '@/models';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils';

const UnitShiftMarkerInfoItemRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 8,

  '& .header': {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: colors.black[100],
  },

  '& .dot': {
    height: 1,
    width: 1,
    borderRadius: '50%',
    backgroundColor: '#e53935',
    margin: '0 6px',
  },
  '& .status-text': {
    display: 'flex',
    alignItems: 'center',
    color: '#0E3145',
    fontWeight: 400,
    fontSize: '0.85rem',
  },

  '& .address': {
    marginTop: 8,
    color: '#1a1a1a',
    fontSize: '0.85rem',
    fontWeight: 400,
  },

  '& .updated': {
    marginTop: 4,
    color: '#444',
    fontSize: '0.85rem',
  },

  '& .divider': {
    marginTop: '12px',
    marginBottom: '6px',
    borderBottom: '1.4px solid rgb(212, 212, 212)',
    borderRadius: '6px',
    width: '100%',
  },
  '& .txt-unit-name': {
    fontSize: '0.875rem',
    color: colors.black[100],
    fontWeight: 500,
  },

  '& .cfs-number': {
    borderRadius: 6,
    padding: '2px 8px',
    marginLeft: 8,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : '#E7EAEC',
  },

  '& .txt-cfs-number': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.text.primary
        : colors.black[80],
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: '18px',
  },

  '& .txt-description': {
    color: colors.black[80],
    fontSize: '0.75rem',
    fontWeight: 400,
  },
}));

interface UnitShiftMarkerInfoItemProps {
  unitName: string;
  unitEmployees?: IUnitShiftResource[];
  cfsNumber?: string;
  cfsDescription?: string;
  vehicleName?: string;
  timestamp?: string;
  address?: IAddress;
  isOffline?: boolean;
}
function getTimeAgoShort(date: string | undefined): string {
  if (!date) return '';
  return formatDistanceToNowStrict(new Date(date), { addSuffix: false });
}

export const UnitShiftMarkerInfoItem = ({
  unitName,
  unitEmployees,
  cfsNumber,
  cfsDescription,
  vehicleName,
  timestamp,
  isOffline,
  address,
}: UnitShiftMarkerInfoItemProps) => {
  if (isOffline) {
    return (
      <UnitShiftMarkerInfoItemRoot>
        <div className="header">
          {unitName}
          <div className="dot" />
          <div className="status-text">
            <WifiOffIcon
              style={{ fontSize: 16, marginRight: 4, color: '#e53935' }}
            />
            Offline
          </div>
        </div>
        <div className="divider" />
        <Typography className="address">{address?.normalized || ''}</Typography>
        <Typography className="updated">
          Last updated: {getTimeAgoShort(timestamp)} ago
        </Typography>
      </UnitShiftMarkerInfoItemRoot>
    );
  }
  return (
    <UnitShiftMarkerInfoItemRoot>
      <div className="header">
        <Typography className="txt-unit-name">{unitName}</Typography>
        {!!cfsNumber && (
          <div className="cfs-number">
            <Typography className="txt-cfs-number">{cfsNumber}</Typography>
          </div>
        )}
      </div>
      {!!unitEmployees &&
        unitEmployees.map((employee) => (
          <>
            <Typography className="txt-description">
              Name: {formatPersonName(employee)}
            </Typography>

            <Typography className="txt-description">
              Badge #:{' '}
              {employee.profile?.employmentInformation?.badgeNumber || 'N/A'}
            </Typography>
            <Typography className="txt-description">
              Vehicle Name: {vehicleName || 'N/A'}
            </Typography>
            <Divider />
          </>
        ))}
      {!!cfsDescription && (
        <Typography className="txt-description">{cfsDescription}</Typography>
      )}
    </UnitShiftMarkerInfoItemRoot>
  );
};
